import {
  createRouter,
  createWebHistory,
  RouteMeta,
  RouteLocationNormalized,
  RouteRecordSingleView,
  RouteRecordSingleViewWithChildren,
  RouteRecordMultipleViews,
  RouteRecordMultipleViewsWithChildren,
  RouteRecordRedirect,
  RouteParams,
} from "vue-router";
import store from "@/store";
import { Actions } from "@/store/enums/StoreEnums";
import { UserRole } from "@/core/services/api/enums/UserRole";
import { SystemOverviewSubpages } from "@/interfaces/admin/SystemOverviewSubpages";
import { PortalServiceEnum } from "@/core/services/api/interfaces/dict/PortalService";
import { Theme } from "@/store/enums/ThemeEnum";
import { TransactionMethod } from "@/core/services/api/enums/TransactionMethod";
import { FilterMode } from "@/interfaces/manager/TransactionFilterParams";

export interface BankingRouteMeta extends RouteMeta {
  title: string | ((route: BankingRouteInterface) => string);
  auth?: boolean | null;
  roles?: UserRole[];
  is_allowed?: (route: BankingRouteInterface) => boolean;
}

type RouteContract =
  | RouteRecordSingleView
  | RouteRecordSingleViewWithChildren
  | RouteRecordMultipleViews
  | RouteRecordMultipleViewsWithChildren
  | RouteRecordRedirect;

export type BankingRouteInterface = RouteContract & {
  meta?: BankingRouteMeta;
  children?: Array<BankingRouteInterface>;
  params?: RouteParams;
};

function queryNumber(
  route: RouteLocationNormalized,
  keys: string[] = []
): null | number {
  for (const option of keys) {
    if (route.query?.[option] && !Number.isNaN(route.query[option] as string)) {
      return Number(route.query[option]);
    }
  }

  return null;
}

function queryArrayNumber(
  route: RouteLocationNormalized,
  keys: string[] = []
): number[] {
  const result: number[] = [];
  keys = keys.reduce((acc: string[], key) => {
    if (key.endsWith("[]")) {
      acc.push(key, key.slice(0, -2)); // Add both "key[]" and "key" to check for array and single values
    } else {
      acc.push(key, `${key}[]`); // Add both "key" and "key[]" for flexibility
    }
    return acc;
  }, []);
  const keysSet = new Set(keys);

  for (const option of keysSet.values()) {
    if (!route.query?.[option]) {
      continue;
    }
    const options = route.query[option];

    if (Array.isArray(options)) {
      options.forEach((item) => {
        if (!Number.isNaN(item as string)) {
          result.push(Number(item));
        }
      });
    } else if (!Number.isNaN(route.query[option] as string)) {
      result.push(Number(route.query[option] as string));
    }
  }

  return result;
}

const parseParamsForTransactionsTable = (route: RouteLocationNormalized) => {
  const bank_account_id = queryNumber(route, ["bank_account_id", "bid"]);
  const defaultMode = FilterMode.APPROVED_TRANSACTIONS;
  const mode = (
    "mode" in route.params ? route.params.mode || defaultMode : defaultMode
  ) as FilterMode;

  console.log({ mode });
  const company_id = queryNumber(route, ["company_id", "cid"]);
  let transaction_id = null as number[] | null;
  if ("tid[]" in route.query && route.query["tid[]"]) {
    if (Array.isArray(route.query["tid[]"])) {
      transaction_id = route.query["tid[]"].map((id) => Number(id));
    } else if (!Number.isNaN(route.query["tid[]"] as string)) {
      transaction_id = [Number.parseFloat(route.query["tid[]"] as string)];
    }
  }

  return {
    mode,
    company_id,
    bank_account_id,
    transaction_id,
  };
};

const routes: Array<BankingRouteInterface> = [
  {
    path: "/admin",
    component: () => import("@/layout/Layout.vue"),
    children: [
      {
        path: "/admin",
        name: "admin.dashboard",
        meta: {
          auth: true,
          title: "Dashboard",
          roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
        },
        component: () => import("@/views/admin/AdminDashboard.vue"),
      },
      {
        path: "/admin/profile",
        name: "admin.profile",
        meta: {
          auth: true,
          title: "Profile",
          roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
        },
        component: () => import("@/views/common/profile/UpdateProfile.vue"),
      },
      {
        path: "/admin/settings",
        name: "admin.settings",
        meta: {
          auth: true,
          title: "Settings",
          roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
        },
        component: () => import("@/views/admin/settings/Settings.vue"),
      },
      {
        path: "/admin/transactions/:transaction_id/history",
        name: "admin.transactions.history",
        meta: {
          auth: true,
          title: "History",
          roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
        },
        component: () =>
          import("@/views/admin/transactions/TransactionHistory.vue"),
      },
      {
        path: "/admin/transactions/credit",
        name: "admin.transactions.credit",
        meta: {
          auth: true,
          title: "History",
          roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
        },
        component: () =>
          import("@/views/admin/transactions/TransactionCreate.vue"),
      },
      {
        path: "/admin/transactions/:mode([pending_transactions|approved_transactions|admin_action_require_transactions]+)",
        name: "admin.transactions.list",
        meta: {
          auth: true,
          title: (route) => {
            switch (route.params?.mode) {
              case "pending_transactions":
                return "Pending Transactions";
              case "approved_transactions":
                return "Approved Transactions";
              case "admin_action_require_transactions":
                return "External Payment Approval";
              default:
                return "Transactions";
            }
          },
          roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
        },
        props: parseParamsForTransactionsTable,
        component: () =>
          import("@/views/admin/transactions/TransactionsTable.vue"),
      },
      {
        path: "/admin/timelines/:entity_class",
        name: "admin.timelines.index",
        props: (route) => {
          return {
            entity_class: route.params.entity_class,
          };
        },
        meta: {
          auth: true,
          title: (route) => {
            if (!route.params?.entity_class) {
              return "not found";
            }

            const entityClass = store.getters.timelineEntityClassBySlug(
              route.params.entity_class
            );

            return entityClass.name;
          },
          roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
        },
        component: () => import("@/views/admin/timelines/EntityTable.vue"),
      },
      {
        path: "/admin/timelines/:entity_class/:id(\\d+)",
        name: "admin.timelines.list",
        props: (route) => {
          return {
            entity_class: route.params.entity_class,
            id: route.params.id,
          };
        },
        meta: {
          auth: true,
          title: (route) => {
            if (!route.params?.entity_class) {
              return "not found";
            }

            const entityClass = store.getters.timelineEntityClassBySlug(
              route.params.entity_class
            );

            return entityClass.name;
          },
          roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
        },
        component: () => import("@/views/admin/timelines/EntityTimeline.vue"),
      },
      {
        path: "/admin/auth-checkpoints",
        name: "admin.auth-checkpoints.list",
        meta: {
          auth: true,
          title: "Check Points",
          roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
        },
        component: () =>
          import("@/views/admin/auth-checkpoints/AuthCheckpointsList.vue"),
      },
      {
        path: "/admin/white-label",
        name: "admin.white-label.index",
        meta: {
          auth: true,
          title: "(Default) White Label",
          roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
        },
        component: () =>
          import("@/views/common/profile/white-label/WhiteLabelDetails.vue"),
      },
      {
        path: "/admin/white-label/documents",
        name: "admin.white-label.documents",
        meta: {
          auth: true,
          title: "(Default) White Label Documents",
          roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
        },
        component: () =>
          import("@/views/common/profile/white-label/WhiteLabelDocuments.vue"),
      },
      {
        path: "/admin/kyc-file-archives",
        name: "admin.kyc-file-archives.list",
        meta: {
          auth: true,
          title: "KYC file archives",
          roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
        },
        component: () => import("@/views/admin/archives/KycArchiveList.vue"),
      },
      {
        path: "/admin/announcements",
        name: "admin.announcements.list",
        meta: {
          auth: true,
          title: "Announcements",
          roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
        },
        component: () =>
          import("@/views/admin/announcements/AnnouncementList.vue"),
      },
      {
        path: "/admin/announcements/create",
        name: "admin.announcements.create",
        meta: {
          auth: true,
          title: "Create Announcement",
          roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
        },
        component: () =>
          import("@/views/admin/announcements/AnnouncementCreate.vue"),
      },
      {
        path: "/admin/announcements/:id(\\d+)",
        name: "admin.announcements.update",
        meta: {
          auth: true,
          title: "Update Announcement",
          roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
        },
        component: () =>
          import("@/views/admin/announcements/AnnouncementUpdate.vue"),
      },
      {
        path: "/admin/activity-logs",
        name: "admin.activity-logs.list",
        meta: {
          auth: true,
          title: "Activity logs",
          roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
        },
        component: () =>
          import("@/views/admin/activity-logs/ActivityLogsList.vue"),
      },
      {
        path: "/admin/bank-accounts/integrity",
        name: "admin.bank-accounts.integrity",
        meta: {
          auth: true,
          title: "Bank account integrities",
          roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
        },
        component: () =>
          import("@/views/admin/bank-accounts/IntegrityCheckList.vue"),
      },
      {
        path: `/admin/system/:subpage([${Object.keys(
          SystemOverviewSubpages
        ).join("|")}]+)`,
        name: "admin.system.overview",
        meta: {
          auth: true,
          title: (route) => {
            const subpage = route.params?.subpage as string;
            if (!subpage) {
              return "System Overview";
            }

            return `System Overview: ${SystemOverviewSubpages[subpage]}`;
          },
          roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
        },
        component: () => import("@/views/admin/system/Overview.vue"),
      },
      {
        path: "/admin/users",
        name: "admin.users",
        meta: {
          auth: true,
          title: "Users",
          roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
        },
        component: () => import("@/views/admin/users/AdminUsers.vue"),
      },
      {
        path: "/admin/users/:user_id",
        name: "admin.users.profile",
        meta: {
          auth: true,
          title: "View User",
          roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
        },
        component: () => import("@/views/admin/users/AdminUsersProfile.vue"),
      },
      {
        path: "/admin/company-list",
        name: "admin.companies.list",
        meta: {
          auth: true,
          title: "Company list",
          roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
        },
        component: () => import("@/views/admin/company/CompanyList.vue"),
      },
      {
        path: "/admin/company-profile/:company_id",
        name: "admin.companies.profile",
        meta: {
          auth: true,
          title: "Company profile",
          roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
        },
        component: () => import("@/views/admin/company/CompanyView.vue"),
        props: (route) => {
          return {
            id: route.params.company_id,
            zoho_id: null,
          };
        },
      },
      {
        path: "/admin/users/:user_id/edit",
        name: "admin.users.edit",
        meta: {
          title: "Update User",
          auth: true,
          roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
        },
        component: () =>
          import("@/views/admin/users/AdminUsersUpdateProfile.vue"),
      },
    ],
  },
  {
    path: "/iframe-widget",
    component: () => import("@/layout/IframeWidget.vue"),
    children: [
      {
        path: "/iframe-widget/company-profile",
        name: "iframe-widget.companies.profile", //if you want to change the name of the route - update related BootstrapConfig
        meta: {
          title: "Company profile",
        },
        component: () => import("@/views/admin/company/CompanyView.vue"),
        props: () => {
          return {
            id: null,
            zoho_id: store.getters.getZohoIframePageLoadInfoValue("EntityId"),
          };
        },
      },
    ],
  },
  {
    path: "/",
    name: "manager",
    redirect: "/manager",
    component: () => import("@/layout/Layout.vue"),
    children: [
      {
        path: "/manager",
        name: "manager.dashboard",
        meta: {
          auth: true,
          title: "Dashboard",
          roles: [UserRole.MANAGER_READER, UserRole.MANAGER],
        },
        component: () => import("@/views/manager/ManagerDashboard.vue"),
      },
      {
        path: "/menu",
        name: "menu",
        meta: {
          auth: true,
          title: "Menu",
        },
        component: () => import("@/views/authorization/Menu.vue"),
      },
      {
        path: "/manager/profile",
        name: "manager.profile",
        meta: {
          auth: true,
          title: "Profile",
          roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
        },
        component: () => import("@/views/common/profile/UpdateProfile.vue"),
      },
      {
        path: "/manager/white-label",
        name: "manager.white-label.index",
        meta: {
          auth: true,
          title: "White Label",
          roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
        },
        component: () =>
          import("@/views/common/profile/white-label/WhiteLabelDetails.vue"),
      },
      {
        path: "/manager/white-label/documents",
        name: "manager.white-label.documents",
        meta: {
          auth: true,
          title: "White Label Documents",
          roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
        },
        component: () =>
          import("@/views/common/profile/white-label/WhiteLabelDocuments.vue"),
      },
      {
        path: "/manager/portal-services/:portal_service",
        name: "manager.portal-services",
        meta: {
          auth: true,
          title: "Portal Services",
          roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
          is_allowed: (): boolean =>
            store.getters.isIndividualOrHas2FA &&
            store.getters.isCompanyEnabled &&
            store.getters.isCompanyFeaturePortalsEnabled,
        },
        props: (route) => {
          const theme = store.getters.theme;

          return {
            portal_service:
              route.params.portal_service ||
              (Theme.IBANFLEX === theme
                ? PortalServiceEnum.KATBIT
                : PortalServiceEnum.IBANFLEX),
          };
        },
        component: () => import("@/views/common/profile/PortalServices.vue"),
      },
      {
        path: "/manager/psp-portal",
        name: "manager.psp-portal",
        meta: {
          auth: true,
          title: "PSP Portal",
          roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
          is_allowed: (): boolean =>
            store.getters.isIndividualOrHas2FA &&
            store.getters.isCompanyEnabled &&
            store.getters.isCompanyFeaturePspPortalEnabled,
        },
        component: () => import("@/components/psp-portal/Navigation.vue"),
      },
      {
        path: "/manager/psp-portal/accounts",
        name: "manager.psp-portal.accounts",
        meta: {
          auth: true,
          title: "PSP Portal: Accounts",
          roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
          is_allowed: (): boolean =>
            store.getters.isIndividualOrHas2FA &&
            store.getters.isCompanyEnabled &&
            store.getters.isCompanyFeaturePspPortalEnabled,
        },
        component: () =>
          import("@/views/common/profile/psp-portal/Accounts.vue"),
      },
      {
        path: "/manager/psp-portal/sign-up",
        name: "manager.psp-portal.sign-up",
        meta: {
          auth: true,
          title: "PSP Portal: Sign-Up",
          roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
          is_allowed: (): boolean =>
            store.getters.isIndividualOrHas2FA &&
            store.getters.isCompanyEnabled &&
            store.getters.isCompanyFeaturePspPortalEnabled,
        },
        component: () => import("@/views/common/profile/psp-portal/SignUp.vue"),
      },
      {
        path: "/manager/psp-portal/integration",
        name: "manager.psp-portal.integration",
        meta: {
          auth: true,
          title: "PSP Portal: Integration",
          roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
          is_allowed: (): boolean =>
            store.getters.isIndividualOrHas2FA &&
            store.getters.isCompanyEnabled &&
            store.getters.isCompanyFeaturePspPortalEnabled,
        },
        component: () =>
          import("@/views/common/profile/psp-portal/Integration.vue"),
      },
      {
        path: "/manager/accounts/:currency_type?",
        name: "manager.accounts.list",
        meta: {
          auth: true,
          title: "Bank accounts",
          roles: [UserRole.MANAGER_READER, UserRole.MANAGER],
          is_allowed: (): boolean =>
            store.getters.isIndividualOrHas2FA &&
            store.getters.isCompanyEnabled,
        },
        component: () => {
          if (store.getters.isThemeKatbit) {
            return import(
              "@/themes/katbit/views/manager/bank-accounts/BankAccountList.vue"
            );
          }

          return import("@/views/manager/bank-accounts/BankAccountsView.vue");
        },
      },
      {
        path: "/manager/transactions/:mode([pending_transactions|approved_transactions]+)",
        name: "manager.transactions.list",
        meta: {
          auth: true,
          title: (route) => {
            return route.params?.mode === "pending_transactions"
              ? "Pending Transactions"
              : "Approved Transactions";
          },
          roles: [UserRole.MANAGER_READER, UserRole.MANAGER],
          is_allowed: (): boolean =>
            store.getters.isIndividualOrHas2FA &&
            store.getters.isCompanyEnabled,
        },
        props: parseParamsForTransactionsTable,
        component: () =>
          import("@/views/manager/transactions/TransactionsTable.vue"),
      },
      {
        path: "/manager/affiliates",
        name: "manager.affiliates",
        meta: {
          auth: true,
          title: "Sub-accounts",
          roles: [UserRole.MANAGER_READER, UserRole.MANAGER],
        },
        children: [
          {
            path: "/manager/affiliates/compliance-trackings",
            name: "manager.affiliates.compliance-trackings",
            meta: {
              auth: true,
              title: "Sub-accounts: Compliance Tracking",
              roles: [UserRole.MANAGER_READER, UserRole.MANAGER],
              is_allowed: (): boolean =>
                store.getters.isIndividualOrHas2FA &&
                store.getters.isCompanyEnabled &&
                store.getters.isCompanyFeatureAffiliationEnabled,
            },
            component: () =>
              import(
                "@/views/manager/affiliates/compliance-trackings/ComplianceTrackingList.vue"
              ),
          },
          {
            path: "/manager/affiliates/transactions/:mode([pending_transactions|approved_transactions]+)",
            name: "manager.affiliates.transactions",
            meta: {
              auth: true,
              title: (route) => {
                return route.params?.mode === "pending_transactions"
                  ? "Sub-accounts: Pending Transactions"
                  : "Sub-accounts: Approved Transactions";
              },
              roles: [UserRole.MANAGER_READER, UserRole.MANAGER],
              is_allowed: (): boolean =>
                store.getters.isIndividualOrHas2FA &&
                store.getters.isCompanyEnabled &&
                store.getters.isCompanyFeatureAffiliationEnabled,
            },
            props: parseParamsForTransactionsTable,
            component: () =>
              import(
                "@/views/manager/affiliates/transactions/TransactionsTable.vue"
              ),
          },
        ],
      },
      {
        path: "/manager/white-labeled",
        name: "manager.white-labeled",
        meta: {
          auth: true,
          title: "White-label: Accounts",
          roles: [UserRole.MANAGER_READER, UserRole.MANAGER],
        },
        children: [
          {
            path: "/manager/white-labeled/compliance-trackings",
            name: "manager.white-labeled.compliance-trackings",
            meta: {
              auth: true,
              title: "White-label: Compliance Tracking",
              roles: [UserRole.MANAGER_READER, UserRole.MANAGER],
              is_allowed: (): boolean =>
                store.getters.isIndividualOrHas2FA &&
                store.getters.isCompanyEnabled &&
                store.getters.isCompanyWhiteLabelEnabled,
            },
            component: () =>
              import(
                "@/views/manager/white-labeled/compliance-trackings/ComplianceTrackingList.vue"
              ),
          },
          {
            path: "/manager/white-labeled/transactions/:mode([pending_transactions|approved_transactions]+)",
            name: "manager.white-labeled.transactions",
            meta: {
              auth: true,
              title: (route) => {
                return route.params?.mode === "pending_transactions"
                  ? "White-label: Pending Transactions"
                  : "White-label: Approved Transactions";
              },
              roles: [UserRole.MANAGER_READER, UserRole.MANAGER],
              is_allowed: (): boolean =>
                store.getters.isIndividualOrHas2FA &&
                store.getters.isCompanyEnabled &&
                store.getters.isCompanyWhiteLabelEnabled,
            },
            props: parseParamsForTransactionsTable,
            component: () =>
              import(
                "@/views/manager/white-labeled/transactions/TransactionsTable.vue"
              ),
          },
        ],
      },
      {
        path: "/manager/transactions/:action([create|repeat|return]+)/:method([iban|card|crypto|import|transfer|exchange|convert]+)/:transaction_id?",
        name: "manager.transactions.create",
        meta: {
          auth: true,
          title: (route) => {
            const map = {
              iban: "Create IBAN transaction",
              card: "Create card transaction",
              crypto: "Create crypto transaction",
              import: "Import transactions",
              transfer: "Create transfer",
              exchange: "Create exchange",
              convert: "Convert currencies",
            };
            const method = route.params?.method as TransactionMethod;
            if (!method) {
              return "Create transaction";
            }
            if (!(method in map)) {
              return "Create transaction";
            }

            return map[method];
          },
          roles: [UserRole.MANAGER],
          is_allowed: (): boolean =>
            store.getters.isIndividualOrHas2FA &&
            store.getters.isCompanyEnabled,
        },
        props: (route) => {
          let amount = null as number | null;
          if (route.query.a && !Number.isNaN(route.query.a as string)) {
            amount = Number.parseFloat(route.query.a as string);
          }
          let account_id = null as number | null;
          if (route.query.bid && !Number.isNaN(route.query.bid as string)) {
            account_id = Number.parseInt(route.query.bid as string);
          }
          let agent_id = null as number | null;
          if (
            route.query.agent_id &&
            !Number.isNaN(route.query.agent_id as string)
          ) {
            agent_id = Number.parseInt(route.query.agent_id as string);
          }
          let to_account_id = null as number | null;
          if (route.query.tid && !Number.isNaN(route.query.tid as string)) {
            to_account_id = Number.parseInt(route.query.tid as string);
          }

          const ps = route.query?.ps as string | undefined;
          let is_flexpsp_deposit = false;

          if (
            route.query.is_flexpsp_deposit &&
            Boolean(route.query.is_flexpsp_deposit)
          ) {
            is_flexpsp_deposit = true;
          }

          return {
            method: route.params.method,
            action: route.params.action,
            transaction_id: route.params.transaction_id,
            portal_service:
              ps &&
              Object.values(PortalServiceEnum).includes(ps as PortalServiceEnum)
                ? (ps as PortalServiceEnum)
                : null,
            portal_service_remote_account: route.query.psra,
            portal_service_currency: route.query.pscu,
            is_flexpsp_deposit,
            amount,
            account_id,
            to_account_id,
            agent_id,
          };
        },
        component: () =>
          import("@/views/manager/transactions/CreateTransaction.vue"),
      },
      {
        path: "/manager/transaction-agents",
        name: "manager.agents.list",
        meta: {
          auth: true,
          title: "Beneficiaries",
          roles: [UserRole.MANAGER_READER, UserRole.MANAGER],
          is_allowed: (): boolean =>
            store.getters.isIndividualOrHas2FA &&
            store.getters.isCompanyEnabled,
        },
        component: () =>
          import("@/views/manager/transactions/TransactionAgentsTable.vue"),
      },
      {
        path: "/manager/send-funds-affiliate",
        name: "manager.send-funds-affiliate",
        meta: {
          auth: true,
          title: "Send funds to affiliate",
          roles: [UserRole.MANAGER_READER, UserRole.MANAGER],
          is_allowed: (): boolean =>
            store.getters.isIndividualOrHas2FA &&
            store.getters.isCompanyEnabled &&
            store.getters.isAffiliated,
        },
        component: () =>
          import(
            "@/views/manager/transactions/SendFundsAffiliatedToAffiliate.vue"
          ),
      },
      {
        path: "/manager/transaction-agents/:id",
        name: "manager.agents.update",
        meta: {
          auth: true,
          title: "Update beneficiary",
          roles: [UserRole.MANAGER_READER, UserRole.MANAGER],
          is_allowed: (): boolean =>
            store.getters.isIndividualOrHas2FA &&
            store.getters.isCompanyEnabled,
        },
        component: () =>
          import("@/views/manager/transactions/TransactionAgentForm.vue"),
      },
      {
        path: "/manager/transaction-agents/create",
        name: "manager.agents.create",
        meta: {
          auth: true,
          title: "Create beneficiary",
          roles: [UserRole.MANAGER_READER, UserRole.MANAGER],
          is_allowed: (): boolean =>
            store.getters.isIndividualOrHas2FA &&
            store.getters.isCompanyEnabled,
        },
        component: () =>
          import("@/views/manager/transactions/TransactionAgentForm.vue"),
      },
      {
        path: "/manager/compliance-trackings",
        name: "manager.kyc.list",
        meta: {
          auth: true,
          title: "Compliance trackings",
          roles: [UserRole.MANAGER_READER, UserRole.MANAGER],
          is_allowed: (): boolean =>
            store.getters.isIndividualOrHas2FA &&
            store.getters.isCompanyEnabled,
        },
        component: () =>
          import(
            "@/views/manager/compliance-trackings/ComplianceTrackingList.vue"
          ),
      },
      {
        path: "/manager/reports",
        name: "manager.reports.list",
        meta: {
          auth: true,
          title: "Reports",
          roles: [UserRole.MANAGER_READER, UserRole.MANAGER],
          is_allowed: (): boolean =>
            store.getters.userHasTwoFactorAuth &&
            store.getters.isCompanyEnabled,
        },
        props: (route) => {
          return {
            bank_account_id: queryArrayNumber(route, [
              "bank_account_id",
              "bid",
            ]),
            report_id: queryArrayNumber(route, ["report_id", "rid"]),
          };
        },
        component: () => import("@/views/manager/reports/ReportList.vue"),
      },
      {
        path: "/manager/notifications",
        name: "manager.notifications.list",
        meta: {
          auth: true,
          title: "Notifications",
          roles: [UserRole.MANAGER_READER, UserRole.MANAGER],
          is_allowed: (): boolean =>
            store.getters.isIndividualOrHas2FA &&
            store.getters.isCompanyEnabled,
        },
        component: () =>
          import("@/views/manager/notifications/NotificationList.vue"),
      },
      {
        path: "/manager/imports",
        name: "manager.imports.list",
        meta: {
          auth: true,
          title: "Imports",
          roles: [UserRole.MANAGER_READER, UserRole.MANAGER],
          is_allowed: (): boolean =>
            store.getters.userHasTwoFactorAuth &&
            store.getters.isCompanyEnabled,
        },
        component: () => import("@/views/manager/imports/ImportList.vue"),
      },
      {
        path: "/manager/support/tickets/list",
        name: "manager.support.tickets.list",
        meta: {
          auth: true,
          title: "Tickets",
          roles: [UserRole.MANAGER_READER, UserRole.MANAGER],
          is_allowed: (): boolean =>
            store.getters.isIndividualOrHas2FA &&
            store.getters.isCompanyEnabled,
        },
        component: () => import("@/views/manager/support/TicketList.vue"),
      },
      {
        path: "/manager/support/tickets/:ticket_id",
        name: "manager.support.tickets.item",
        meta: {
          auth: true,
          title: "View ticket",
          roles: [UserRole.MANAGER_READER, UserRole.MANAGER],
          is_allowed: (): boolean =>
            store.getters.isIndividualOrHas2FA &&
            store.getters.isCompanyEnabled,
        },
        component: () => import("@/views/manager/support/TicketItem.vue"),
      },
      {
        path: "/manager/compliance-trackings/:entity([transaction|user|company|lead]+)/:entity_id(\\d+)/:kyc_id?",
        name: "manager.compliance-trackings.list",
        meta: {
          auth: true,
          title: "Entity compliance tracking",
          roles: [UserRole.MANAGER_READER, UserRole.MANAGER],
          is_allowed: (): boolean =>
            store.getters.isIndividualOrHas2FA &&
            store.getters.isCompanyEnabled,
        },
        component: () =>
          import(
            "@/views/manager/compliance-trackings/ComplianceTrackingView.vue"
          ),
      },
      {
        path: "/manager/crypto/receive",
        name: "manager.crypto.deposit",
        meta: {
          auth: true,
          title: "Deposit Crypto",
          roles: [UserRole.MANAGER_READER, UserRole.MANAGER],
          is_allowed: (): boolean =>
            store.getters.userHasTwoFactorAuth &&
            store.getters.isCompanyEnabled &&
            store.getters.isCompanyCryptoEnabled,
        },
        props: (route) => {
          return {
            bank_account_id: queryNumber(route, ["bank_account_id", "bid"]),
            to_account_id: queryNumber(route, ["to_account_id", "tid"]),
          };
        },
        component: () => import("@/views/manager/crypto/CryptoReceive.vue"),
      },
      {
        path: "/manager/crypto/buy",
        name: "manager.crypto.buy",
        meta: {
          auth: true,
          title: "Buy Crypto",
          roles: [UserRole.MANAGER_READER, UserRole.MANAGER],
          is_allowed: (): boolean =>
            store.getters.userHasTwoFactorAuth &&
            store.getters.isCompanyEnabled &&
            store.getters.isCompanyCryptoEnabled,
        },
        props: (route) => {
          return {
            bank_account_id: queryNumber(route, ["bank_account_id", "bid"]),
            to_account_id: queryNumber(route, ["to_account_id", "tid"]),
          };
        },
        component: () => import("@/views/manager/crypto/CryptoBuy.vue"),
      },
      {
        path: "/manager/crypto/withdraw",
        name: "manager.crypto.withdraw",
        meta: {
          auth: true,
          title: "Withdraw Crypto",
          roles: [UserRole.MANAGER_READER, UserRole.MANAGER],
          is_allowed: (): boolean =>
            store.getters.userHasTwoFactorAuth &&
            store.getters.isCompanyEnabled &&
            store.getters.isCompanyCryptoEnabled,
        },
        props: (route) => {
          return {
            bank_account_id: queryNumber(route, ["bank_account_id", "bid"]),
          };
        },
        component: () => import("@/views/manager/crypto/CryptoWithdraw.vue"),
      },
      {
        path: "/manager/crypto/sell",
        name: "manager.crypto.sell",
        meta: {
          auth: true,
          title: "Sell Crypto",
          roles: [UserRole.MANAGER_READER, UserRole.MANAGER],
          is_allowed: (): boolean =>
            store.getters.userHasTwoFactorAuth &&
            store.getters.isCompanyEnabled &&
            store.getters.isCompanyCryptoEnabled,
        },
        props: (route) => {
          return {
            bank_account_id: queryNumber(route, ["bank_account_id", "bid"]),
          };
        },
        component: () => import("@/views/manager/crypto/CryptoSell.vue"),
      },
    ],
  },
  {
    path: "/",
    meta: {
      title: "Home",
    },
    component: () => import("@/components/page-layouts/Auth.vue"),
    children: [
      {
        path: "/login",
        name: "login",
        meta: {
          auth: false,
          title: "Login",
        },
        props: (route) => {
          let oauth_code = null as string | null;
          let email = null as string | null;
          if ("oauth_code" in route.query && route.query.oauth_code) {
            oauth_code = route.query.oauth_code as string;
          }
          if ("email" in route.query && route.query.email) {
            email = route.query.email as string;
          }

          return {
            oauth_code,
            email,
          };
        },
        component: () => import("@/views/authorization/Login.vue"),
      },
      {
        path: "/rocket-chat/auth",
        name: "rocket-chat.auth",
        meta: {
          auth: true,
          title: "Rocket Chat Auth",
        },
        component: () => import("@/views/manager/rocket-chat/Auth.vue"),
      },
      {
        path: "/purchase/:product_hash",
        name: "guest.products.purchase",
        meta: {
          auth: false,
          title: "Purchase product",
        },
        component: () => import("@/views/guest/PurchaseProduct.vue"),
      },
      {
        path: "/registration/:affiliation?",
        name: "registration",
        meta: {
          auth: false,
          title: "Registration",
        },
        component: () => import("@/views/authorization/Registration.vue"),
      },
      {
        path: "/legacy/:key",
        name: "legacy",
        meta: {
          auth: false,
          title: "Login from legacy",
        },
        component: () => import("@/views/authorization/FromLegacy.vue"),
      },
      {
        path: "/flexpsp/auth/:user_id/:auth_key",
        name: "flexpsp-quick-login",
        meta: {
          auth: null,
          title: "Quick Login from FlexPSP",
        },
        props: (route) => {
          return {
            user_id: route.params?.user_id
              ? Number(route.params.user_id)
              : null,
            auth_key: route.params.auth_key,
          };
        },
        component: () => import("@/views/authorization/QuickLoginFlexPsp.vue"),
      },
      {
        path: "/portals/:portal/:key",
        name: "remote-portal",
        meta: {
          auth: false,
          title: "Login from remote portal",
        },
        component: () => import("@/views/authorization/FromRemotePortal.vue"),
      },
      {
        path: "/u/email/:key/:code",
        name: "approve-email",
        meta: {
          auth: false,
          title: "Approve Email",
        },
        component: () => import("@/views/authorization/ApproveEmail.vue"),
      },
      {
        path: "/forgot-password",
        name: "forgot-password",
        meta: {
          auth: false,
          title: "Forgot password",
        },
        component: () => import("@/views/authorization/ForgotPassword.vue"),
      },
      {
        path: "/reset-password",
        name: "reset-password",
        meta: {
          auth: false,
          title: "Reset password",
        },
        component: () => import("@/views/authorization/ResetPassword.vue"),
      },
    ],
  },
  {
    path: "/docs",
    component: () => import("@/layout/Blank.vue"),
    children: [
      {
        path: "/docs/v1",
        name: "guest.v1.documentation",
        meta: {
          auth: null,
          title: "Documentation",
        },
        component: () => import("@/views/api/v1/Documentation.vue"),
      },
      {
        path: "/docs/v1/swagger",
        name: "guest.v1.swagger",
        meta: {
          auth: null,
          title: "Swagger OpenAPI",
        },
        component: () => import("@/views/api/v1/Swagger.vue"),
      },
      {
        path: "/docs/v1/changelog",
        name: "guest.v1.changelog",
        meta: {
          auth: null,
          title: "Changelog",
        },
        component: () => import("@/views/api/v1/Changelog.vue"),
      },
    ],
  },
  {
    path: "/",
    component: () => import("@/layout/Clean.vue"),
    children: [
      {
        path: "/profile/compliance-trackings/:service([files|video|form|final]+)?/:id?",
        name: "profile.verifications",
        meta: {
          auth: true,
          roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
          title: "User compliance tracking",
          is_allowed: (): boolean =>
            store.getters.isUserComplianceTrackingRequired,
        },
        component: () =>
          import("@/views/manager/user-verifications/Wizard.vue"),
      },
    ],
  },
  {
    // the 404 route, when none of the above matches
    path: "/404",
    name: "404",
    meta: {
      auth: null,
      title: "Not Found",
    },
    component: () => import("@/views/errors/Error404.vue"),
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/404",
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});
router.beforeEach(async (route) => {
  return store.dispatch(Actions.COMMON_PROJECT_BOOTSTRAP_BEFORE_ROUTE, route);
});

router.afterEach(async (route) => {
  return store.dispatch(Actions.COMMON_PROJECT_BOOTSTRAP_AFTER_ROUTE, route);
});

export default router;
